import { useEffect, useState, Dispatch, SetStateAction } from 'react';
import { useSubModule } from '../contexts/SubModuleContext';

interface UseSubModuleViewOptions<T extends string> {
  /** Verdi som betyr «landingsside / ingen undermodul» (default = initial). */
  landing?: T;
  /** Hvis satt: kun disse ?sub=-verdiene honoreres ved dyplenke (resten ignoreres trygt). */
  validSubs?: readonly string[];
}

/**
 * Drop-in erstatning for `useState` i moduler med interne undervisninger.
 * Samler all den generiske boilerplaten for den globale ModuleIndicator:
 *
 *  - Publiserer aktiv undervisning til SubModuleContext (vises/kopieres av indikatoren).
 *  - Nullstiller på `landing`-verdien og ved unmount.
 *  - Åpner undervisning direkte fra `?sub=`-parameteren ved mount (dyplenke).
 *
 * Bruk: bytt
 *   const [view, setView] = useState<string>('overview');
 * med
 *   const [view, setView] = useSubModuleView<string>('overview', { landing: 'overview', validSubs: VIEW_IDS });
 *
 * Ingen annen plumbing trengs — én linje per modul.
 */
export function useSubModuleView<T extends string>(
  initial: T,
  options?: UseSubModuleViewOptions<T>
): [T, Dispatch<SetStateAction<T>>] {
  const { landing = initial, validSubs } = options ?? {};
  const { setSubModuleId } = useSubModule();
  const [view, setView] = useState<T>(initial);

  // Dyplenke: åpne undervisning fra ?sub= ved mount
  useEffect(() => {
    const sub = new URLSearchParams(window.location.search).get('sub');
    if (sub && (!validSubs || validSubs.includes(sub))) {
      setView(sub as T);
    }
    // Kun ved mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Publiser aktiv undervisning til den globale indikatoren
  useEffect(() => {
    setSubModuleId(view === landing ? null : view);
    return () => setSubModuleId(null);
  }, [view, landing, setSubModuleId]);

  return [view, setView];
}
